import { useTranslation } from "react-i18next";
import { Cell, Customized, Legend, Pie, PieChart, Tooltip } from "recharts";

import { CustomizedPieLabel } from "modules/LocationReports/components";
import "modules/LocationReports/components/TotalPieChart/TotalPieChart.scss";
import { getTranslationOrValue } from "modules/LocationReports/utils";
import { REPORTS_COLORS } from "utils/common";

import type { ReportColor } from "models";

type TotalPieChartProps = {
  data: Record<string, string>[];
  colors: ReportColor[];
};

export const TotalPieChart = ({ data, colors }: TotalPieChartProps) => {
  const { t } = useTranslation();

  const renderCustomLabel = () => (
    <text x={155} y={165} dy={11} textAnchor="middle" fontSize="22" fontWeight="bold" fill="#595959">
      <tspan x="405">
        {t("v8_total", "Total")} {data.reduce((a, b) => a + (Number(b.value) || 0), 0)}
      </tspan>
      {data.length > 0 && data.at(0)?.total && (
        <tspan x="405" dy="30">
          {t("v8_WO", "WO")} {data.at(0)?.total}
        </tspan>
      )}
    </text>
  );

  const renderLegend = () => {
    const numberOfColumns = data.length <= 20 ? 1 : 2;

    return (
      <ul className="total-pie-chart-legend" style={{ gridTemplateColumns: `repeat(${numberOfColumns}, 1fr)` }}>
        {data.map((entry, index) => (
          <li
            key={`item-${index}`}
            style={{ color: colors.find(col => (getTranslationOrValue(t, col.name) || col.name) === entry.name)?.color || REPORTS_COLORS[index % REPORTS_COLORS.length] }}
          >
            {getTranslationOrValue(t, entry.name)}
          </li>
        ))}
      </ul>
    );
  };

  const reportColors = data.map((entry, index) => {
    return colors.find(col => (getTranslationOrValue(t, col.name) || col.name) === entry.name)?.color || REPORTS_COLORS[index % REPORTS_COLORS.length];
  });

  return (
    <PieChart width={1300} height={400} className="TotalPieChart">
      <Pie
        data={data}
        cx={400}
        cy={175}
        innerRadius={80}
        outerRadius={120}
        labelLine={false}
        label={props => <CustomizedPieLabel {...props} colors={reportColors} />}
        fill="#8884d8"
        dataKey="value"
        style={{ outline: "none" }}
      >
        {data.map((entry, index) => (
          <Cell key={index} fill={colors.find(col => (t(`v8_${col.name}`) || col.name) === entry.name)?.color || REPORTS_COLORS[index % REPORTS_COLORS.length]} />
        ))}
      </Pie>

      <Legend verticalAlign="middle" align="right" layout="vertical" content={renderLegend} />
      <Customized component={renderCustomLabel} />
      <Tooltip
        content={({ active, payload, label }) => {
          if (!active || !payload?.length) return null;

          return (
            <div className="tooltip-wrapper">
              <p className="tooltip-label">{label}</p>
              {payload.map((entry, index) => (
                <ul key={index} className="tooltip-item-list">
                  <p style={{ color: entry.color }}>{`${getTranslationOrValue(t, String(entry.name))}: ${entry.value}`}</p>
                </ul>
              ))}
            </div>
          );
        }}
      />
    </PieChart>
  );
};
